import {CommonModule} from '@angular/common';
import {Component, Inject, OnInit} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {TuiAlertService, TuiDialogContext} from '@taiga-ui/core';
import {POLYMORPHEUS_CONTEXT} from '@taiga-ui/polymorpheus';
import {Observable} from 'rxjs';
import {DomainSetting} from '@gatewee/types/domain-setting';
import {RecipientSetting} from '@gatewee/types/recipient-setting';
import {GateweeButtonComponent} from '../../gatewee-button/gatewee-button.component';
import {GateweeInputV2Component} from '../../gatewee-input-v2/gatewee-input-v2.component';

interface DialogData {
  id: number;
  setting: RecipientSetting | DomainSetting;
  editSettingFn: (
    id: number,
    setting: RecipientSetting | DomainSetting,
  ) => Observable<void>;
}

@Component({
  selector: 'string-parameter',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    GateweeButtonComponent,
    GateweeInputV2Component,
  ],
  templateUrl: './string-parameter.component.html',
})
export class StringParameterComponent implements OnInit {
  formGroup!: FormGroup;
  submitting = false;
  id!: number;
  setting!: RecipientSetting | DomainSetting;
  editSettingFn!: (
    id: number,
    setting: RecipientSetting | DomainSetting,
  ) => Observable<void>;

  isObjectValue = false;
  stringKeys: string[] = [];

  constructor(
    private fb: FormBuilder,
    private alerts: TuiAlertService,
    private translate: TranslateService,
    @Inject(POLYMORPHEUS_CONTEXT)
    private readonly context: TuiDialogContext<boolean, DialogData>,
  ) {
    this.id = this.context.data.id;
    this.setting = this.context.data.setting;
    this.editSettingFn = this.context.data.editSettingFn;
  }

  ngOnInit(): void {
    this.initializeForm();
  }

  private initializeForm(): void {
    let parsedValue: unknown = '';

    if (this.setting.value) {
      try {
        if (typeof this.setting.value === 'string') {
          // Try to parse as JSON, if fails treat as plain string
          try {
            parsedValue = JSON.parse(this.setting.value);
          } catch {
            parsedValue = this.setting.value;
          }
        } else {
          parsedValue = this.setting.value;
        }
      } catch (e) {
        // If parsing fails, treat as string
        parsedValue = String(this.setting.value || '');
      }
    }

    // Check if it's an object with string properties
    if (
      typeof parsedValue === 'object' &&
      parsedValue !== null &&
      !Array.isArray(parsedValue)
    ) {
      this.isObjectValue = true;
      const parsedObj = parsedValue as Record<string, unknown>;
      this.stringKeys = Object.keys(parsedObj);
      const formControls: Record<string, [string]> = {};

      this.stringKeys.forEach(key => {
        const value = parsedObj[key];
        formControls[key] = [String(value || '')];
      });

      this.formGroup = this.fb.group(formControls);
    } else {
      // Single string value
      this.isObjectValue = false;
      const stringValue = String(parsedValue || '');

      this.formGroup = this.fb.group({
        value: [stringValue, Validators.required],
      });
    }
  }

  async onSubmit() {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    this.submitting = true;

    let newValue: string | Record<string, unknown>;

    if (this.isObjectValue) {
      // For object values, create an object from form values
      newValue = this.formGroup.value as Record<string, unknown>;
    } else {
      // For single string, get the value from form
      const stringValue = this.formGroup.get('value')?.value || '';
      newValue = stringValue;
    }

    // Create updated setting with proper type
    const updatedSetting = {
      ...this.setting,
      value: newValue,
    } as RecipientSetting | DomainSetting;

    this.editSettingFn(this.id, updatedSetting).subscribe({
      next: () => {
        this.alerts
          .open(
            this.translate.instant('features.settings.updateSuccess', {
              key: this.setting.key,
            }),
            {
              label: this.translate.instant('common.status.success'),
              appearance: 'success',
            },
          )
          .subscribe();
        this.context.completeWith(true);
      },
      error: (error: Error) => {
        console.error('Error updating string setting:', error);
        this.alerts
          .open(
            this.translate.instant('features.settings.updateError', {
              key: this.setting.key,
            }),
            {
              label: this.translate.instant('common.status.error'),
              appearance: 'error',
            },
          )
          .subscribe();
        this.submitting = false;
      },
    });
  }

  close() {
    this.context.completeWith(false);
  }

  getSettingLabel(key: string): string {
    return this.translate.instant(
      `features.settings.parameters.${this.setting.key}.${key}.label`,
    );
  }

  getSettingPlaceholder(key: string): string {
    return (
      this.translate.instant(
        `features.settings.parameters.${this.setting.key}.${key}.placeholder`,
      ) || ''
    );
  }
}
